import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Volume2, VolumeX, Play, Pause, RotateCcw } from 'lucide-react';
import { textToSpeechService } from '../../services/tts/textToSpeechService';
import { useVoice } from '../../contexts/VoiceContext';
import KaTeXRenderer from '../common/KaTeXRenderer';

interface EnglishContentRendererProps {
  content: {
    vocabulary?: Array<{
      id: string;
      word: string;
      phonetic: string;
      meaning: string;
      example: string;
      exampleTranslation: string;
    }>;
    sentences?: Array<{
      id: string;
      text: string;
      translation: string;
    }>;
    paragraphs?: Array<{
      id: string;
      text: string;
      translation: string;
    }>;
  };
  className?: string;
}

interface TTSState {
  isPlaying: boolean;
  currentText: string;
  currentType: 'word' | 'sentence' | 'paragraph' | null;
}

const EnglishContentRenderer: React.FC<EnglishContentRendererProps> = ({ 
  content, 
  className = '' 
}) => {
  const [isTTSEnabled, setIsTTSEnabled] = useState(true);
  const [isTTSSupported, setIsTTSSupported] = useState(false);
  const [ttsState, setTtsState] = useState<TTSState>({
    isPlaying: false,
    currentText: '',
    currentType: null
  });
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Get selected voice from context
  const { selectedVoice } = useVoice();

  useEffect(() => {
    // Check TTS support
    setIsTTSSupported('speechSynthesis' in window);
    
    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      textToSpeechService.stop();
    };
  }, []);

  const handleToggleTTS = useCallback(() => {
    setIsTTSEnabled(prev => !prev);
    if (ttsState.isPlaying) {
      handleStop();
    }
  }, [ttsState.isPlaying]);

  const handleStop = useCallback(() => {
    textToSpeechService.stop();
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setTtsState({
      isPlaying: false,
      currentText: '',
      currentType: null
    });
  }, []);


  const handleSpeak = useCallback(async (
    text: string, 
    type: 'word' | 'sentence' | 'paragraph'
  ) => {
    if (!isTTSEnabled || !isTTSSupported) {
      console.warn('TTS is not available');
      return;
    }

    try {
      // Stop current speech
      textToSpeechService.stop();
      
      // Create new abort controller for this speech
      abortControllerRef.current = new AbortController();
      
      setTtsState({
        isPlaying: true,
        currentText: text,
        currentType: type
      });

      // Use selected voice or fallback to best voice
      const voiceToUse = selectedVoice || textToSpeechService.getBestEnglishVoice();
      
      // Detect mobile for optimal settings
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // Choose appropriate TTS options based on type
      const options = {
        voice: voiceToUse,
        preferredProvider: 'google' as const,
        preferredGender: 'female' as const,
      };

      let speakPromise: Promise<void>;
      switch (type) {
        case 'word':
          speakPromise = textToSpeechService.speakWord(text, {
            ...options,
            speed: isMobile ? 1.0 : 1.0 // Mobile: Always 1.0
          });
          break;
        case 'sentence':
          speakPromise = textToSpeechService.speakSentence(text, {
            ...options,
            speed: isMobile ? 1.0 : 0.85 // Mobile: Always 1.0
          });
          break;
        case 'paragraph':
          speakPromise = textToSpeechService.speakParagraph(text, {
            ...options,
            speed: isMobile ? 1.0 : 0.8 // Mobile: Always 1.0
          });
          break;
        default:
          speakPromise = textToSpeechService.speak(text, options);
      }

      await speakPromise;
      
      // Only update state if this speech wasn't aborted
      if (!abortControllerRef.current?.signal.aborted) {
        setTtsState({
          isPlaying: false,
          currentText: '',
          currentType: null
        });
      }
      
    } catch (error) {
      console.error('Error speaking text:', error);
      setTtsState({
        isPlaying: false,
        currentText: '',
        currentType: null
      });
    }
  }, [isTTSEnabled, isTTSSupported, selectedVoice]);

  const renderVocabulary = () => {
    if (!content.vocabulary || content.vocabulary.length === 0) {
      return null;
    }

    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-blue-500 pb-2">
          📚 Vocabulary
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {content.vocabulary.map((vocab) => (
            <div
              key={vocab.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{vocab.word}</h3>
                <button
                  onClick={() => handleSpeak(vocab.word, 'word')}
                  className="text-gray-400 hover:text-blue-500 transition-colors"
                  disabled={!isTTSEnabled || !isTTSSupported}
                >
                  {ttsState.isPlaying && ttsState.currentText === vocab.word ? (
                    <VolumeX size={18} />
                  ) : (
                    <Volume2 size={18} />
                  )}
                </button>
              </div>
              <p className="text-sm text-gray-500 font-mono mb-1">{vocab.phonetic}</p>
              <p className="text-gray-700 font-medium mb-2">{vocab.meaning}</p>
              <p className="text-sm text-gray-500 italic mb-1">
                <KaTeXRenderer content={`"${vocab.example}"`} />
              </p>
              <p className="text-sm text-gray-600">
                <KaTeXRenderer content={`"${vocab.exampleTranslation}"`} />
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSentences = () => {
    if (!content.sentences || content.sentences.length === 0) {
      return null;
    }

    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-green-500 pb-2">
          💬 Sentences
        </h2>
        <div className="space-y-4">
          {content.sentences.map((sentence) => (
            <div
              key={sentence.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="text-gray-900 text-lg leading-relaxed flex-1">
                  <KaTeXRenderer content={sentence.text} />
                </div>
                <button
                  onClick={() => handleSpeak(sentence.text, 'sentence')}
                  className="ml-4 text-gray-400 hover:text-blue-500 transition-colors flex-shrink-0"
                  disabled={!isTTSEnabled || !isTTSSupported}
                >
                  {ttsState.isPlaying && ttsState.currentText === sentence.text ? (
                    <VolumeX size={20} />
                  ) : (
                    <Volume2 size={20} />
                  )}
                </button>
              </div>
              <div className="text-gray-600 italic">
                <KaTeXRenderer content={sentence.translation} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderParagraphs = () => {
    if (!content.paragraphs || content.paragraphs.length === 0) {
      return null;
    }

    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-purple-500 pb-2">
          📖 Paragraphs
        </h2>
        <div className="space-y-6">
          {content.paragraphs.map((paragraph) => (
            <div
              key={paragraph.id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="text-gray-900 text-lg leading-relaxed mb-4">
                    <KaTeXRenderer content={paragraph.text} />
                  </div>
                  <div className="text-gray-600 italic leading-relaxed">
                    <KaTeXRenderer content={paragraph.translation} />
                  </div>
                </div>
                <button
                  onClick={() => handleSpeak(paragraph.text, 'paragraph')}
                  className="ml-4 text-gray-400 hover:text-blue-500 transition-colors flex-shrink-0"
                  disabled={!isTTSEnabled || !isTTSSupported}
                >
                  {ttsState.isPlaying && ttsState.currentText === paragraph.text ? (
                    <VolumeX size={20} />
                  ) : (
                    <Volume2 size={20} />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!content || (!content.vocabulary && !content.sentences && !content.paragraphs)) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <p className="text-gray-500">No content available</p>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* TTS Control Panel */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-semibold text-blue-900">Text-to-Speech</h3>
            <button
              onClick={handleToggleTTS}
              className={`
                flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium
                transition-all duration-200
                ${isTTSEnabled 
                  ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              {isTTSEnabled ? (
                <>
                  <Volume2 className="w-4 h-4" />
                  <span>TTS Enabled</span>
                </>
              ) : (
                <>
                  <VolumeX className="w-4 h-4" />
                  <span>TTS Disabled</span>
                </>
              )}
            </button>
          </div>
          
          {ttsState.isPlaying && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-blue-700">
                Speaking: {ttsState.currentText.substring(0, 30)}...
              </span>
              <button
                onClick={handleStop}
                className="flex items-center space-x-1 px-2 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200 transition-colors"
              >
                <VolumeX className="w-4 h-4" />
                <span>Stop</span>
              </button>
            </div>
          )}
        </div>
        
        {isTTSEnabled && (
          <p className="text-sm text-blue-700 mt-3">
            Click the speaker icon next to any word, sentence, or paragraph to hear it spoken aloud. Voice selected on main page is used.
          </p>
        )}
      </div>

      {/* Vocabulary Section */}
      {renderVocabulary()}

      {/* Sentences Section */}
      {renderSentences()}

      {/* Paragraphs Section */}
      {renderParagraphs()}
    </div>
  );
};

export default EnglishContentRenderer;
